import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/primitives';
import { PlusOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import NotificationTable from '../components/NotificationTable';
import { useDeleteNotification } from '../hooks/useNotificationMutations';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { userApi } from '@/features/users/api/user.api';

export default function NotificationListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const list = useDynamicAdminList<any>(ADMIN_FILTER_RESOURCES.notifications, {
    initialSortField: 'createdAt',
    initialSortDir: 'desc',
  });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteNotification();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'user.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('roomAds.searchUser'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader
        title={t('notifications.title')}
        subtitle={t('notifications.subtitle')}
        action={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/notifications/create')}>
            {t('notifications.add')}
          </Button>
        }
      />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data}
        value={list.filters}
        onChange={list.setFilters}
        onReset={list.resetFilters}
        relationLookups={relationLookups}
      />
      <div style={{ marginTop: 16 }}>
        <NotificationTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onEdit={(id: number) => navigate(`/notifications/${id}/edit`)}
          onDelete={(item: any) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog open={!!deleteTarget} title={t('notifications.deleteTitle')}
        message={t('notifications.deleteMessage', { title: deleteTarget?.titleEn })}
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending} />
    </div>
  );
}
